import { useState } from 'react';
import { FileText, Calendar } from '@/utils/icons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Period } from '@/types/metrics';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (period: Period, customRange?: { from: Date; to: Date }) => void;
  activeFilters: {
    profile?: string | null;
    vendor?: string | null;
    version?: string | null;
    portfolio?: string | null;
  };
}

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'custom', label: 'Custom' },
];

function periodLabel(period: Period): string {
  switch (period) {
    case '24h':
      return 'Last 24 Hours';
    case '7d':
      return 'Last 7 Days';
    case '30d':
      return 'Last 30 Days';
    case '90d':
      return 'Last 90 Days';
    case 'custom':
      return 'Custom Range';
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export { periodLabel };

export function ExportDialog({ open, onOpenChange, onExport, activeFilters }: ExportDialogProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const filterChips = [
    activeFilters.profile && `Profile: ${activeFilters.profile}`,
    activeFilters.vendor && `Hardware: ${activeFilters.vendor}`,
    activeFilters.version && `Version: ${activeFilters.version}`,
    activeFilters.portfolio && `Portfolio: ${activeFilters.portfolio}`,
  ].filter(Boolean) as string[];

  const canExport = selectedPeriod !== 'custom' || (customFrom && customTo);

  async function handleExport() {
    setExporting(true);
    try {
      const customRange =
        selectedPeriod === 'custom' && customFrom && customTo
          ? { from: new Date(customFrom), to: new Date(customTo) }
          : undefined;
      await onExport(selectedPeriod, customRange);
      onOpenChange(false);
    } finally {
      setExporting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:dls-max-w-md !dls-left-auto !dls-top-4 !dls-right-4 !dls-translate-x-0 !dls-translate-y-0">
        <DialogHeader>
          <DialogTitle className="dls-flex dls-items-center dls-gap-2">
            <FileText className="dls-h-5 dls-w-5" />
            Export Health Summary
          </DialogTitle>
          <DialogDescription>
            Select a time period for the uptime trend data included in the report.
          </DialogDescription>
        </DialogHeader>

        <div className="dls-space-y-4 dls-py-2">
          {/* Time Period Selector */}
          <div className="dls-space-y-2">
            <label className="dls-text-sm dls-font-medium">Time Period</label>
            <div className="dls-flex dls-flex-wrap dls-gap-2">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedPeriod(opt.value)}
                  className={cn(
                    'dls-px-3 dls-py-1.5 dls-rounded-md dls-text-sm dls-font-medium dls-border dls-transition-colors',
                    selectedPeriod === opt.value
                      ? 'dls-bg-primary dls-text-primary-foreground dls-border-primary'
                      : 'dls-bg-background dls-text-foreground dls-border-border hover:dls-bg-muted'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range */}
          {selectedPeriod === 'custom' && (
            <div className="dls-flex dls-items-center dls-gap-3">
              <div className="dls-flex-1 dls-space-y-1">
                <label className="dls-text-xs dls-text-muted-foreground">From</label>
                <div className="dls-relative">
                  <Calendar className="dls-absolute dls-left-2.5 dls-top-2.5 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="dls-w-full dls-rounded-md dls-border dls-border-input dls-bg-background dls-pl-9 dls-pr-3 dls-py-2 dls-text-sm"
                  />
                </div>
              </div>
              <div className="dls-flex-1 dls-space-y-1">
                <label className="dls-text-xs dls-text-muted-foreground">To</label>
                <div className="dls-relative">
                  <Calendar className="dls-absolute dls-left-2.5 dls-top-2.5 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="dls-w-full dls-rounded-md dls-border dls-border-input dls-bg-background dls-pl-9 dls-pr-3 dls-py-2 dls-text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Active Filters */}
          {filterChips.length > 0 && (
            <div className="dls-space-y-2">
              <label className="dls-text-sm dls-font-medium">Active Filters</label>
              <div className="dls-flex dls-flex-wrap dls-gap-1.5">
                {filterChips.map((chip) => (
                  <span
                    key={chip}
                    className="dls-inline-flex dls-items-center dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium"
                  >
                    {chip}
                  </span>
                ))}
              </div>
              <p className="dls-text-xs dls-text-muted-foreground">
                The export will only include data matching these filters.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={!canExport || exporting}>
            {exporting ? 'Generating...' : 'Export PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
